import React, { useState, useEffect, useMemo, useRef } from 'react';
import { renderMarkdown } from '../../utils/markdown';
import * as api from '../../api';
import { fingerprintKey } from '../../utils/keyHistory';
import {
  buildTimelineItems,
  DateDivider,
  MessageActionMenu,
  MessageReactionStrip,
  PinnedMessagesStrip,
  ReplyComposerPreview,
  ReplyContext,
  ScrollToLatestButton,
  UnreadDivider,
  messagePreview
} from './MessageActions';

const CHAT_EMOJIS = [
  '\u{1F600}', '\u{1F604}', '\u{1F602}', '\u{1F60A}', '\u{1F60D}', '\u{1F60E}',
  '\u{1F91D}', '\u{1F64F}', '\u{1F44D}', '\u{1F525}', '\u{2728}', '\u{1F680}',
  '\u{1F512}', '\u{1F6E1}\u{FE0F}', '\u{26A1}', '\u{2705}', '\u{2757}', '\u{2764}\u{FE0F}'
];

export const ChatPane = ({
  activeChatContact,
  chatMessages,
  activeIdentity,
  chatInputText,
  setChatInputText,
  handleSendChatMessage,
  setActiveChatContact,
  showEmojiPicker,
  setShowEmojiPicker,
  handleDeleteChatMessage,
  handleClearDirectChat,
  openContactProfile,
  messageMeta = {},
  onToggleMessagePin,
  onToggleMessageSaved,
  onReactToMessage,
  messageReplyTarget,
  setMessageReplyTarget,
  t,
  displayGaiaID,
  getSenderRoles,
  contactPresence,
  unreadMarker
}) => {
  const scrollRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const previousLastMessageIdRef = useRef('');
  const [actionMessageId, setActionMessageId] = useState(null);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [pendingMessageCount, setPendingMessageCount] = useState(0);

  // Message Edits State (Zero-knowledge local persistence)
  const [chatEdits, setChatEdits] = useState(() => {
    if (!activeIdentity?.ID) return {};
    try {
      const stored = localStorage.getItem(`gaia_chat_edits_${activeIdentity.ID}`);
      return stored ? JSON.parse(stored) : {};
    } catch (_) {
      return {};
    }
  });

  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editInputText, setEditInputText] = useState('');
  const [contactTyping, setContactTyping] = useState(false);
  const [copiedFingerprint, setCopiedFingerprint] = useState(false);
  const [securityAlerts, setSecurityAlerts] = useState([]);

  // Blocked contacts state
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    if (activeChatContact && activeIdentity) {
      setIsBlocked(!!activeChatContact.blocked);
    }
  }, [activeChatContact, activeIdentity]);

  const handleBlockContact = async () => {
    if (!activeChatContact || !activeIdentity) return;
    const nextBlocked = !isBlocked;
    setIsBlocked(nextBlocked);
    try {
      await api.saveMailContact({
        id: activeChatContact.id || activeChatContact.ID,
        gaiaId: activeChatContact.gaiaID,
        displayName: activeChatContact.displayName,
        publicKey: activeChatContact.publicKey || '',
        blocked: nextBlocked
      });
      const storedKey = `contacts_${activeIdentity.UserID || activeIdentity.userId || 'default'}`;
      const stored = localStorage.getItem(storedKey);
      if (stored) {
        try {
          const list = JSON.parse(stored);
          const updated = list.map(c => c.gaiaID === activeChatContact.gaiaID ? { ...c, blocked: nextBlocked } : c);
          localStorage.setItem(storedKey, JSON.stringify(updated));
        } catch (_) {}
      }
      setActiveChatContact(prev => prev ? { ...prev, blocked: nextBlocked } : prev);
    } catch (_) {
      setIsBlocked(!nextBlocked);
    }
  };

  const handleReportAbuse = () => {
    if (!activeChatContact || !activeIdentity) return;
    // Show a confirmation or call a prompt to get a comment
    const comment = prompt("Bitte geben Sie einen Grund für die Missbrauchsmeldung ein:");
    if (comment === null) return; // Cancelled
    
    // Call backend submitAbuseReport endpoint via fetch or import
    import('../../api').then(async (api) => {
      try {
        await api.submitAbuseReport(
          activeIdentity.ID,
          'user',
          activeChatContact.ID || activeChatContact.gaiaID,
          'harassment',
          'medium',
          null,
          comment
        );
        alert('Vielen Dank. Die Meldung wurde erfolgreich an die Governance-Moderatoren übermittelt.');
      } catch (err) {
        alert('Fehler beim Einreichen des Reports: ' + err.message);
      }
    });
  };

  const appendChatEmoji = emoji => {
    setChatInputText(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const visibleMessages = useMemo(() => {
    if (!activeChatContact || !activeIdentity) return [];
    return chatMessages.filter(msg =>
      (msg.sender === activeChatContact.gaiaID && msg.recipient === activeIdentity.GaiaID) ||
      (msg.sender === activeIdentity.GaiaID && msg.recipient === activeChatContact.gaiaID)
    );
  }, [activeChatContact, activeIdentity, chatMessages]);

  const filteredMessages = useMemo(() => {
    if (!localSearchQuery.trim()) return visibleMessages;
    const query = localSearchQuery.toLowerCase();
    return visibleMessages.filter(msg => {
      const body = String(msg.body || '').toLowerCase();
      const sender = String(msg.senderGaia || msg.sender || '').toLowerCase();
      return body.includes(query) || sender.includes(query);
    });
  }, [localSearchQuery, visibleMessages]);

  const timelineItems = useMemo(() => buildTimelineItems(filteredMessages), [filteredMessages]);
  const lastMessageId = filteredMessages.length > 0 ? filteredMessages[filteredMessages.length - 1].id : '';
  const keyHistory = Array.isArray(activeChatContact?.keyHistory) ? activeChatContact.keyHistory : [];
  const latestKeyEntry = keyHistory[0] || null;
  const hasKeyRotation = keyHistory.length > 1;
  const currentFingerprint = activeChatContact?.trustPassport?.fingerprint
    || latestKeyEntry?.fingerprint
    || fingerprintKey(activeChatContact?.publicKey || '');
  const recentSecurityAlerts = securityAlerts.slice(0, 3);
  const presenceLabel = contactPresence?.isOnline
    ? (contactPresence.status === 'away' ? 'abwesend' : contactPresence.status === 'busy' ? 'beschaeftigt' : 'online')
    : contactPresence?.lastSeenAt
      ? `zuletzt gesehen ${new Date(contactPresence.lastSeenAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : 'offline';

  const syncScrollState = () => {
    if (!scrollRef.current) return;
    const distanceFromBottom = scrollRef.current.scrollHeight - scrollRef.current.scrollTop - scrollRef.current.clientHeight;
    const nearBottom = distanceFromBottom <= 72;
    setIsNearBottom(current => (current === nearBottom ? current : nearBottom));
    if (nearBottom) {
      setPendingMessageCount(0);
    }
  };

  const scrollToLatest = behavior => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior
    });
    setPendingMessageCount(0);
    setIsNearBottom(true);
  };

  useEffect(() => {
    setLocalSearchQuery('');
    setPendingMessageCount(0);
    setIsNearBottom(true);
    previousLastMessageIdRef.current = '';
    window.requestAnimationFrame(() => {
      scrollToLatest('auto');
    });
  }, [activeChatContact?.ID, activeChatContact?.gaiaID]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return undefined;

    syncScrollState();
    const handleScroll = () => syncScrollState();
    node.addEventListener('scroll', handleScroll);

    return () => {
      node.removeEventListener('scroll', handleScroll);
    };
  }, [activeChatContact?.ID, activeChatContact?.gaiaID]);

  useEffect(() => {
    if (!lastMessageId) {
      previousLastMessageIdRef.current = '';
      return;
    }

    if (!previousLastMessageIdRef.current) {
      previousLastMessageIdRef.current = lastMessageId;
      window.requestAnimationFrame(() => {
        scrollToLatest('auto');
      });
      return;
    }

    if (previousLastMessageIdRef.current === lastMessageId) {
      return;
    }

    previousLastMessageIdRef.current = lastMessageId;
    if (isNearBottom) {
      window.requestAnimationFrame(() => {
        scrollToLatest('smooth');
      });
      return;
    }

    setPendingMessageCount(count => count + 1);
  }, [isNearBottom, lastMessageId]);

  useEffect(() => () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!activeIdentity?.ID) {
      setSecurityAlerts([]);
      return undefined;
    }

    let stopped = false;
    const isRelevantSecurityEvent = event => {
      const category = String(event?.category || '').toLowerCase();
      const summary = String(event?.summary || '').toLowerCase();
      return (
        category.includes('replay') ||
        category.includes('tamper') ||
        category.includes('integrity') ||
        summary.includes('replay') ||
        summary.includes('manipulation') ||
        summary.includes('integrity')
      );
    };

    const loadSecurityAlerts = async () => {
      try {
        const result = await api.getSecurityEvents();
        if (!stopped) {
          setSecurityAlerts((result?.events || []).filter(event => !event?.acknowledged_at && isRelevantSecurityEvent(event)));
        }
      } catch (_) {
        if (!stopped) {
          setSecurityAlerts([]);
        }
      }
    };

    loadSecurityAlerts();
    const interval = window.setInterval(loadSecurityAlerts, 15000);
    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
  }, [activeIdentity?.ID]);

  useEffect(() => {
    if (!activeIdentity?.ID || !activeChatContact?.gaiaID) {
      setContactTyping(false);
      return undefined;
    }

    let stopped = false;
    const loadTyping = async () => {
      try {
        const result = await api.getTypingStatus(activeIdentity.ID, {
          peerGaiaId: activeChatContact.gaiaID
        });
        if (!stopped) {
          setContactTyping(!!result?.direct?.isTyping);
        }
      } catch (_) {
        if (!stopped) {
          setContactTyping(false);
        }
      }
    };

    loadTyping();
    const interval = window.setInterval(loadTyping, 2000);
    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
  }, [activeChatContact?.gaiaID, activeIdentity?.ID]);

  useEffect(() => {
    if (!activeIdentity?.ID || !activeChatContact?.gaiaID) {
      return undefined;
    }

    const shouldSignalTyping = !isBlocked && chatInputText.trim().length > 0;
    let cancelled = false;

    const sendTypingState = async isTyping => {
      try {
        await api.updateTypingStatus(activeIdentity.ID, {
          peerGaiaId: activeChatContact.gaiaID,
          isTyping
        });
      } catch (_) {}
    };

    if (!shouldSignalTyping) {
      sendTypingState(false);
      return undefined;
    }

    sendTypingState(true);
    const interval = window.setInterval(() => {
      if (!cancelled) {
        sendTypingState(true);
      }
    }, 2500);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      sendTypingState(false);
    };
  }, [activeChatContact?.gaiaID, activeIdentity?.ID, chatInputText, isBlocked]);

  const openMessageActions = (event, messageId) => {
    event.preventDefault();
    event.stopPropagation();
    setActionMessageId(messageId);
  };

  const startLongPress = messageId => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
    }
    longPressTimerRef.current = window.setTimeout(() => {
      setActionMessageId(messageId);
    }, 520);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const jumpToMessage = messageId => {
    if (!scrollRef.current) return;
    const target = scrollRef.current.querySelector(`[data-message-id="${messageId}"]`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const replyToMessage = message => {
    setMessageReplyTarget({
      messageId: message.id,
      sender: message.sender,
      senderGaia: message.senderGaia,
      bodyPreview: messagePreview(message),
      createdAt: message.createdAt
    });
  };

  const startEditingMessage = message => {
    setEditingMessageId(message.id);
    setEditInputText(chatEdits[message.id] || message.body);
  };

  const saveEditedMessage = (messageId) => {
    if (!editInputText.trim()) return;
    const nextEdits = { ...chatEdits, [messageId]: editInputText };
    setChatEdits(nextEdits);
    if (activeIdentity?.ID) {
      localStorage.setItem(`gaia_chat_edits_${activeIdentity.ID}`, JSON.stringify(nextEdits));
    }
    setEditingMessageId(null);
    setEditInputText('');
  };

  const handleCopyFingerprint = async () => {
    if (!currentFingerprint || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(currentFingerprint);
      setCopiedFingerprint(true);
      window.setTimeout(() => setCopiedFingerprint(false), 1800);
    } catch (_) {}
  };

  if (!activeChatContact) {
    return (
      <div className="chat-container empty-chat-state">
        <h3>{t('kein_chat_ausgewaehlt') || 'Kein Chat ausgewählt'}</h3>
        <p>{t('select_contact_chat') || 'Wähle einen Kontakt aus dem linken Panel aus, um einen quantensicheren E2E-Chat zu starten.'}</p>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <header className="reader-header chat-reader-header">
        <button type="button" className="mobile-back-btn" onClick={() => setActiveChatContact(null)}>
          {'<'} {t('quanten_chat') || 'Chats'}
        </button>
        <div className="chat-peer-header">
          <div className="chat-peer-icon" aria-hidden="true">{'\u{1F4AC}'}</div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" className="link-button contact-name-button" onClick={() => openContactProfile(activeChatContact.gaiaID)}>
                {activeChatContact.displayName}
              </button>
              {getSenderRoles && getSenderRoles(activeChatContact.gaiaID)?.map(role => {
                let bg = 'rgba(255,255,255,0.05)';
                let color = 'var(--text-secondary)';
                let border = '1px solid var(--border-color)';
                let label = role;

                if (role === 'node_operator') {
                  bg = 'rgba(168, 85, 247, 0.15)';
                  color = '#c084fc';
                  border = '1px solid rgba(168, 85, 247, 0.3)';
                  label = t('node_operator') || 'Node Operator';
                } else if (role === 'senior_reviewer') {
                  bg = 'rgba(249, 115, 22, 0.15)';
                  color = '#fdba74';
                  border = '1px solid rgba(249, 115, 22, 0.3)';
                  label = t('senior_reviewer') || 'Senior Reviewer';
                } else if (role === 'trusted_reviewer') {
                  bg = 'rgba(20, 184, 166, 0.15)';
                  color = '#2dd4bf';
                  border = '1px solid rgba(20, 184, 166, 0.3)';
                  label = t('trusted_reviewer') || 'Trusted Reviewer';
                } else {
                  label = t(role) || role;
                }

                return (
                  <span
                    key={role}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '1px 6px',
                      borderRadius: '10px',
                      fontSize: '0.65rem',
                      fontWeight: '600',
                      background: bg,
                      color: color,
                      border: border,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
            <span>{displayGaiaID(activeChatContact.gaiaID)}</span>
            <span
              className="chat-presence-status"
              style={{
                display: 'block',
                marginTop: '2px',
                fontSize: '0.72rem',
                color: contactPresence?.isOnline ? 'var(--accent-cyan)' : 'var(--text-muted)'
              }}
              title={contactPresence?.lastSeenAt ? `Letzter Kontakt: ${new Date(contactPresence.lastSeenAt).toLocaleString()}` : 'Kein Presence-Signal empfangen'}
            >
              {presenceLabel}
            </span>
            {contactTyping && (
              <span className="chat-typing-status">
                tippt gerade...
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            className="input-field"
            placeholder="Chat durchsuchen..."
            value={localSearchQuery}
            onChange={event => setLocalSearchQuery(event.target.value)}
            style={{ maxWidth: '180px', padding: '6px 10px', fontSize: '0.8rem', height: 'auto', margin: 0 }}
          />
          <button type="button" className={`btn-secondary ${isBlocked ? 'btn-danger' : ''}`} onClick={handleBlockContact}>
            {isBlocked ? 'Entblocken' : 'Blockieren'}
          </button>
          <button type="button" className="btn-secondary" onClick={handleReportAbuse}>
            Melden
          </button>
          <button type="button" className="btn-secondary chat-header-action" onClick={handleClearDirectChat}>
            {t('clear_chat') || 'Clear Chat'}
          </button>
        </div>
      </header>

      <section className="chat-security-overview" aria-label="Chat Sicherheit">
        <div className="chat-security-chip chat-security-chip--good" title="Ende-zu-Ende verschluesselt mit GaiaSecure und GaiaShield-Integritaetspruefung">
          <strong>GaiaSecure E2E</strong>
          <span>aktiv</span>
        </div>
        <button
          type="button"
          className="chat-security-chip"
          onClick={handleCopyFingerprint}
          title={currentFingerprint || 'Kein Fingerprint verfuegbar'}
        >
          <strong>Fingerprint</strong>
          <span>{currentFingerprint || 'unbekannt'}</span>
        </button>
        {latestKeyEntry?.confirmed && (
          <div className="chat-security-chip" title={latestKeyEntry.lastSeenAt ? `Zuletzt bestaetigt: ${new Date(latestKeyEntry.lastSeenAt).toLocaleString()}` : 'Schluessel bestaetigt'}>
            <strong>Schluesselstatus</strong>
            <span>bestaetigt</span>
          </div>
        )}
        {copiedFingerprint && (
          <div className="chat-security-chip chat-security-chip--accent">
            <strong>Fingerprint</strong>
            <span>kopiert</span>
          </div>
        )}
      </section>

      {hasKeyRotation && (
        <div className="chat-security-banner chat-security-banner--warning">
          <strong>Schluesselwechsel erkannt.</strong>
          <span>
            Dieser Kontakt hat bereits mehr als einen bekannten Identitaetsschluessel. Vergleiche den Fingerprint vor weiterem Vertrauen.
          </span>
          <button type="button" className="btn-secondary" onClick={() => openContactProfile(activeChatContact.gaiaID)}>
            Fingerprint pruefen
          </button>
        </div>
      )}

      {recentSecurityAlerts.length > 0 && (
        <div className="chat-security-banner chat-security-banner--danger">
          <strong>GaiaShield hat Nachrichtenanomalien blockiert.</strong>
          <div className="chat-security-alert-list">
            {recentSecurityAlerts.map(event => (
              <span key={event.event_id}>{event.summary || event.category}</span>
            ))}
          </div>
        </div>
      )}

      {isBlocked && (
        <div className="smtp-security-banner" style={{ background: 'rgba(255, 59, 48, 0.15)', borderColor: 'var(--danger)', padding: '12px', textAlign: 'center', color: '#ff4d4f' }}>
          <strong>⛔ Dieser Kontakt ist blockiert.</strong> Sie können keine Nachrichten senden oder empfangen.
        </div>
      )}

      <PinnedMessagesStrip
        messages={visibleMessages}
        messageMeta={messageMeta}
        onJumpToMessage={jumpToMessage}
        t={t}
      />

      <div className="chat-messages gaia-scrollbar" ref={scrollRef} onClick={() => setActionMessageId(null)}>
        {timelineItems.map(item => {
          if (item.type === 'divider') {
            return <DateDivider key={item.id} label={item.label} />;
          }

          const msg = item.message;
          const showUnreadDivider = unreadMarker?.firstUnreadMessageId && unreadMarker.firstUnreadMessageId === msg.id;
          const isOutgoing = msg.sender === activeIdentity.GaiaID;
          const meta = messageMeta[msg.id] || {};
          const isEditing = editingMessageId === msg.id;
          const hasBeenEdited = !!chatEdits[msg.id];
          const displayedBody = chatEdits[msg.id] || msg.body;
          const deliveryStatus = msg.isRead
            ? { text: '\u2713\u2713', label: 'Gelesen', color: 'var(--accent-cyan)' }
            : msg.delivered
              ? { text: '\u2713', label: 'Zugestellt', color: 'var(--text-muted)' }
              : { text: '\u2713', label: 'Gesendet', color: 'var(--text-muted)' };

          return (
            <React.Fragment key={msg.id}>
              {showUnreadDivider && <UnreadDivider count={unreadMarker?.count || 0} />}
              <div
                data-message-id={msg.id}
                className={`chat-bubble ${isOutgoing ? 'outgoing' : 'incoming'} ${meta.pinned ? 'pinned' : ''} ${meta.saved ? 'saved' : ''}`}
                onContextMenu={event => openMessageActions(event, msg.id)}
                onTouchStart={() => startLongPress(msg.id)}
                onTouchEnd={cancelLongPress}
                onTouchMove={cancelLongPress}
                style={{ paddingBottom: '16px' }}
              >
                <ReplyContext replyTo={msg.replyTo} t={t} />
                
                {isEditing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                    <textarea
                      className="input-field"
                      value={editInputText}
                      onChange={e => setEditInputText(e.target.value)}
                      style={{ background: 'var(--bg-dark)', minHeight: '60px', resize: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button type="button" className="btn-secondary" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => setEditingMessageId(null)}>Abbrechen</button>
                      <button type="button" className="btn-primary" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => saveEditedMessage(msg.id)}>Speichern</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    {renderMarkdown(displayedBody)}
                    {hasBeenEdited && (
                      <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', display: 'block', fontStyle: 'italic', marginTop: '4px' }}>
                        (bearbeitet)
                      </span>
                    )}
                  </div>
                )}

                <MessageReactionStrip meta={meta} />
                
                <div className="chat-bubble-meta" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
                  <span>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  {meta.saved && <span style={{ color: 'var(--accent-cyan)' }}>{t('message_saved') || 'Saved'}</span>}
                  
                  {isOutgoing && !isEditing && (
                    <button
                      type="button"
                      className="chat-delete-btn"
                      style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: '0.75rem', cursor: 'pointer', padding: 0 }}
                      onClick={() => startEditingMessage(msg)}
                    >
                      Bearbeiten
                    </button>
                  )}

                  <button type="button" className="chat-delete-btn" onClick={() => handleDeleteChatMessage(msg.id)}>
                    {t('delete') || 'Delete'}
                  </button>
                  {msg.untrusted && <span className="chat-untrusted">{t('untrusted') || 'Untrusted'}</span>}
                  
                  {isOutgoing && (
                    <span style={{ fontSize: '0.85rem', color: deliveryStatus.color, marginLeft: '4px', fontWeight: 'bold' }} title={deliveryStatus.label} aria-label={deliveryStatus.label}>
                      {deliveryStatus.text}
                    </span>
                  )}
                </div>

                <MessageActionMenu
                  open={actionMessageId === msg.id}
                  message={msg}
                  meta={meta}
                  onClose={() => setActionMessageId(null)}
                  onTogglePin={onToggleMessagePin}
                  onToggleSave={onToggleMessageSaved}
                  onReply={replyToMessage}
                  onReact={onReactToMessage}
                  t={t}
                />
              </div>
            </React.Fragment>
          );
        })}

        {filteredMessages.length === 0 && (
          <div className="chat-empty-hint">
            {t('chat_start_hint') || 'Starten Sie die Konversation! Alle Chat-Nachrichten sind quantensicher E2E verschlüsselt.'}
          </div>
        )}
      </div>

      <div className="chat-composer-stack">
        <ScrollToLatestButton count={pendingMessageCount} onClick={() => scrollToLatest('smooth')} />
        <ReplyComposerPreview
          replyTarget={messageReplyTarget}
          onClear={() => setMessageReplyTarget(null)}
          t={t}
        />
        <form className="chat-input-row" onSubmit={handleSendChatMessage}>
          <div className="emoji-control">
            <button type="button" className="btn-secondary emoji-toggle" onClick={() => setShowEmojiPicker(prev => !prev)} disabled={isBlocked}>
              {'\u{1F642}'}
            </button>
            {showEmojiPicker && (
              <div className="emoji-picker" role="listbox" aria-label="Emoji Auswahl">
                {CHAT_EMOJIS.map(emoji => (
                  <button type="button" key={emoji} onClick={() => appendChatEmoji(emoji)}>
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
          <input
            type="text"
            className="input-field"
            placeholder={isBlocked ? "Chat blockiert" : (t('chat_input_placeholder') || 'Sichere Nachricht eingeben...')}
            value={chatInputText}
            onChange={event => setChatInputText(event.target.value)}
            disabled={isBlocked}
            required
          />
          <button type="submit" className="btn-primary" disabled={isBlocked}>
            {t('senden') || 'Senden'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatPane;
